"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-client";

const formSchema = z.object({
	email: z.string().email({
		message: "Please enter a valid email address.",
	}),
});

export default function ForgotPassword() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const authClient = useAuth();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsLoading(true);

		try {
			const { error } = await authClient.forgetPassword({
				email: values.email,
				redirectTo: "/reset-password",
			});

			if (error) {
				toast.error(error.message || "Failed to send reset code");
			} else {
				setEmailSent(true);
				toast.success("Reset code sent! Check your email for the OTP code.");
			}
		} catch (_error) {
			toast.error("An error occurred. Please try again later.");
		} finally {
			setIsLoading(false);
		}
	}

	if (emailSent) {
		return (
			<div className="px-4 sm:px-0 max-w-[64rem] mx-auto flex h-screen w-screen flex-col items-center justify-center">
				<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
					<div className="flex flex-col space-y-2 text-center">
						<h1 className="text-2xl font-semibold tracking-tight">
							Check your email
						</h1>
						<p className="text-sm text-muted-foreground">
							We've sent a 6-digit OTP code to {form.getValues("email")}. Enter
							it on the reset password page.
						</p>
					</div>
					<Button
						onClick={() => router.push("/reset-password")}
						className="w-full"
					>
						Continue to reset password
					</Button>
					<p className="px-8 text-center text-sm text-muted-foreground">
						<Link
							href="/login"
							className="hover:text-primary underline underline-offset-4"
						>
							Back to login
						</Link>
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="px-4 sm:px-0 max-w-[64rem] mx-auto flex h-screen w-screen flex-col items-center justify-center">
			<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
				<div className="flex flex-col space-y-2 text-center">
					<h1 className="text-2xl font-semibold tracking-tight">
						Forgot your password?
					</h1>
					<p className="text-sm text-muted-foreground">
						Enter your email address and we'll send you a code to reset your
						password
					</p>
				</div>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Email</FormLabel>
									<FormControl>
										<Input
											placeholder="name@example.com"
											type="email"
											autoComplete="email"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Sending code...
								</>
							) : (
								"Send reset code"
							)}
						</Button>
					</form>
				</Form>
				<p className="px-8 text-center text-sm text-muted-foreground">
					<Link
						href="/login"
						className="hover:text-primary underline underline-offset-4"
					>
						Back to login
					</Link>
				</p>
			</div>
		</div>
	);
}
